'use client';

import { useState } from 'react';

import { useRouter, useSearchParams } from 'next/navigation';

import type { Provider } from '@supabase/supabase-js';

import { isBrowser } from '@kit/shared/utils';
import { Button } from '@kit/ui/button';
import { If } from '@kit/ui/if';
import { Separator } from '@kit/ui/separator';
import { Trans } from '@kit/ui/trans';

import { AuthErrorAlert } from './auth-error-alert';
import { EmailOtpAuthContainer } from './email-otp-auth-container';
import { OauthProviders } from './oauth-providers';
import { PasskeySignInButton } from './passkey-sign-in-button';
import { PasswordSignInContainer } from './password-sign-in-container';

type SignInMethod = 'password' | 'emailOtp';

const PASSKEY_ACTION_BUTTON_CLASS =
  'h-11 rounded-lg !border-zinc-950 !bg-zinc-950 text-base !text-white shadow-sm hover:!border-zinc-900 hover:!bg-zinc-900 hover:!text-white focus-visible:!text-white active:!text-white';

export function SignInMethodsContainer(props: {
  inviteToken?: string;

  paths: {
    callback: string;
    home: string;
    joinTeam: string;
  };

  providers: {
    password: boolean;
    magicLink: boolean;
    emailOtp: boolean;
    oAuth: Provider[];
  };
}) {
  const router = useRouter();
  const nextPath = useSearchParams().get('next') ?? props.paths.home;
  const emailOtpEnabled = props.providers.emailOtp || props.providers.magicLink;
  const [signInMethod, setSignInMethod] = useState<SignInMethod>(() =>
    getInitialSignInMethod(props.providers),
  );
  const [passkeyError, setPasskeyError] = useState<Error | string | null>(null);
  const canSwitchMethod = props.providers.password && emailOtpEnabled;
  const showPassword = props.providers.password && signInMethod === 'password';
  const showEmailOtp = emailOtpEnabled && signInMethod === 'emailOtp';
  const hasPrimaryAuth = props.providers.password || emailOtpEnabled;
  const hasAlternativeAuthOptions =
    canSwitchMethod || props.providers.oAuth.length > 0;

  const redirectUrl = getSignInCallbackUrl({
    callbackPath: props.paths.callback,
    nextPath,
  });

  const onSignIn = () => {
    if (props.inviteToken) {
      const searchParams = new URLSearchParams({
        invite_token: props.inviteToken,
      });

      const joinTeamPath = props.paths.joinTeam + '?' + searchParams.toString();

      router.replace(joinTeamPath);
    } else {
      router.replace(nextPath);
    }
  };

  const renderPasskeyAction = () => (
    <PasskeySignInButton
      className={PASSKEY_ACTION_BUTTON_CLASS}
      onError={setPasskeyError}
      onSignIn={onSignIn}
      showError={false}
    />
  );

  return (
    <>
      <If condition={showPassword}>
        <PasswordSignInContainer
          onSignIn={onSignIn}
          passkeyAction={renderPasskeyAction()}
        />
      </If>

      <If condition={showEmailOtp}>
        <EmailOtpAuthContainer
          inviteToken={props.inviteToken}
          onVerified={onSignIn}
          redirectUrl={redirectUrl}
          shouldCreateUser={false}
          passkeyAction={renderPasskeyAction()}
        />
      </If>

      <AuthErrorAlert error={passkeyError} />

      <If condition={hasPrimaryAuth && hasAlternativeAuthOptions}>
        <Separator />
      </If>

      <If condition={hasAlternativeAuthOptions}>
        <OauthProviders
          enabledProviders={props.providers.oAuth}
          inviteToken={props.inviteToken}
          shouldCreateUser={false}
          paths={{
            callback: props.paths.callback,
            returnPath: props.paths.home,
          }}
        >
          <If condition={canSwitchMethod}>
            <Button
              className={'flex w-full space-x-2 text-center'}
              data-test={'email-otp-switch-button'}
              type={'button'}
              variant={'outline'}
              onClick={() => {
                setPasskeyError(null);
                setSignInMethod(showPassword ? 'emailOtp' : 'password');
              }}
            >
              <If
                condition={showPassword}
                fallback={<Trans i18nKey={'auth:signInWithPassword'} />}
              >
                <Trans i18nKey={'auth:signInWithOtp'} />
              </If>
            </Button>
          </If>
        </OauthProviders>
      </If>
    </>
  );
}

function getInitialSignInMethod(providers: {
  password: boolean;
  magicLink: boolean;
  emailOtp: boolean;
}) {
  if (providers.emailOtp || providers.magicLink) {
    return 'emailOtp';
  }

  if (providers.password) {
    return 'password';
  }

  return 'password';
}

function getSignInCallbackUrl({
  callbackPath,
  nextPath,
}: {
  callbackPath: string;
  nextPath: string;
}) {
  if (!isBrowser()) {
    return '';
  }

  const url = new URL(callbackPath, window.location.origin);
  url.searchParams.set('next', nextPath);

  return url.toString();
}
